import axios from "axios";
import Keycloak from "keycloak-js";
import * as Sentry from "@sentry/react";

export const axiosInstance = axios.create();

export const addTokenRefreshInterceptors = (keycloak: Keycloak) => {
  // Request interceptor to refresh the token if it will be expired soon
  axiosInstance.interceptors.request.use(
    async (config) => {
      config.headers.Authorization = `Bearer ${keycloak.token}`;

      return config;
    },
    (error) => error,
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!error.response) {
        console.error("CORS ERROR");

        if (window.location.pathname !== "/maintenance") {
          window.location.href = "/maintenance";
        }

        return Promise.reject(error);
      }
      // If the error status is 401 and there is no originalRequest._retry flag,
      // it means the token has expired and we need to refresh it
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          await keycloak.updateToken(-1);
          originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
          return axios(originalRequest);
        } catch {
          keycloak.logout();
        }
      } else if (error.response?.status === 401 && originalRequest._retry) {
        // If the error status is 401 and the originalRequest._retry flag is set,
        // it means the token has expired and the refresh token request has failed
        // We need to logout the user
        keycloak.logout();
      } else if (error.response?.status === 400) {
        // Get the error message from the response metadata
        // and set it as the error message to show in the UI
        if (error.response?.data?.metadata?.message) {
          error.message = error.response.data.metadata.message;
          error.statusCode = error.response?.status;
        }
      } else if (error.response?.status === 504) {
        // Handle 504 Gateway Timeout errors (server timeout)
        error.message = "Server timeout. Please try again later.";
        error.statusCode = 504;
      } else if (
        error.code === "ECONNABORTED" ||
        error.message.includes("timeout")
      ) {
        // Handle axios timeout errors (client-side timeout when no response is received)
        error.message =
          "Connection timeout. Please check your internet connection and try again.";
        error.statusCode = 408; // Request Timeout
      }

      // Log all non-401 errors to Sentry (exclude auth errors as they're expected)
      if (error.response?.status !== 401) {
        Sentry.captureException(error, {
          tags: {
            errorType: "API_ERROR",
            statusCode: error.response?.status || error.statusCode || "unknown",
          },
          extra: {
            url: error.config?.url,
            method: error.config?.method,
            responseData: error.response?.data,
            errorMessage: error.message,
          },
        });
      }

      return error;
    },
  );
};

export const setAxiosLanguage = (lang: string) => {
  axiosInstance.defaults.headers.common["Accept-Language"] = lang;
};
